import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import ProgressStepper, { RESPONDENT_STEPS } from "../components/ProgressStepper";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Building2 } from "lucide-react";

export default function Consent() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [consent, setConsent] = useState(null);
  const [granted, setGranted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Org capture — soft one-time prompt for historic accounts that predate
  // the registration-time capture. Opens on this page after user info loads
  // and only if the profile has no organization value yet.
  const [orgPromptOpen, setOrgPromptOpen] = useState(false);
  const [orgValue, setOrgValue] = useState("");
  const [savingOrg, setSavingOrg] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!user.organization) setOrgPromptOpen(true);
    api.get("/consent/active", { params: { language: user.preferred_language || "en" } })
      .then((r) => {
        setConsent(r.data);
        const initial = {};
        r.data.purposes.forEach((p) => { initial[p.key] = !!p.required; });
        setGranted(initial);
      })
      .catch(() => toast.error("Could not load consent"));
  }, [user, loading, nav]);

  const saveOrg = async () => {
    const trimmed = orgValue.trim();
    if (!trimmed) {
      toast.error("Please enter your organization (or skip for now).");
      return;
    }
    setSavingOrg(true);
    try {
      await api.post("/users/me/organization", { organization: trimmed });
      toast.success("Organization saved");
      setOrgPromptOpen(false);
      if (typeof refresh === "function") { await refresh(); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save organization");
    } finally {
      setSavingOrg(false);
    }
  };

  const submit = async () => {
    if (!consent) return;
    const missing = consent.purposes.filter((p) => p.required && !granted[p.key]);
    if (missing.length) {
      toast.error("Please accept all required purposes to proceed");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/consent/grant", {
        consent_version_id: consent.consent_id,
        granted_purposes: Object.keys(granted).filter((k) => granted[k]),
      });
      toast.success("Consent recorded");
      nav("/voice");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save consent");
    } finally {
      setSubmitting(false);
    }
  };

  if (!consent) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container-narrow py-20 text-muted-foreground">Loading consent document…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <ProgressStepper steps={RESPONDENT_STEPS} current="consent" />

        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight">{consent.title}</h1>
            <p className="text-muted-foreground mt-3">Please read carefully before granting your consent.</p>
          </div>
          <Badge variant="outline" className="shrink-0" data-testid="consent-version-badge">
            v{consent.version} · {consent.language.toUpperCase()}
          </Badge>
        </div>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="whitespace-pre-line text-sm md:text-base leading-relaxed text-foreground" data-testid="consent-body">
              {consent.body}
            </div>

            <div className="border-t border-border/60 pt-6">
              <div className="flex items-center gap-2 mb-4 text-sm font-medium">
                <ShieldCheck size={16} className="text-primary" /> Purpose-specific consent
              </div>
              <div className="space-y-4">
                {consent.purposes.map((p) => (
                  <label key={p.key} className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={!!granted[p.key]}
                      onCheckedChange={(v) => setGranted((g) => ({ ...g, [p.key]: !!v }))}
                      data-testid={`consent-purpose-${p.key}`}
                    />
                    <div>
                      <div className="text-sm font-medium">
                        {p.label}
                        {p.required && <span className="text-destructive ml-1">*</span>}
                      </div>
                      {p.required && <div className="text-xs text-muted-foreground">Required</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3 mt-8">
          <Button size="lg" disabled={submitting} onClick={submit}
            className="rounded-full px-8 mano-lift" data-testid="consent-agree-btn">
            {submitting ? "Recording…" : "I agree — continue"}
          </Button>
          <Button size="lg" variant="ghost" onClick={() => nav("/")}
            className="rounded-full px-6" data-testid="consent-decline-btn">
            Decline
          </Button>
        </div>
      </main>

      <Dialog open={orgPromptOpen} onOpenChange={setOrgPromptOpen}>
        <DialogContent data-testid="org-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={16} /> Where do you go / work?
            </DialogTitle>
            <DialogDescription>
              A quick one-time question — tell us your school, workplace or
              clinic so we can group anonymised trends without ever tying them
              back to you. You can skip and add it later from your profile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <Label htmlFor="org-prompt-input">Organization</Label>
            <Input id="org-prompt-input"
                   value={orgValue}
                   onChange={(e) => setOrgValue(e.target.value)}
                   placeholder="e.g. Northgate Wellness Center"
                   data-testid="org-prompt-input" />
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setOrgPromptOpen(false)}
                    data-testid="org-prompt-skip">
              Skip for now
            </Button>
            <Button onClick={saveOrg} disabled={savingOrg}
                    className="rounded-full mano-lift"
                    data-testid="org-prompt-save">
              {savingOrg ? "Saving…" : "Save organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
